<template>
	<div v-if="isMobileOrTablet" :class="context.classes.inner">
		<select
			:id="context.id"
			:value="context._value ?? ''"
			:class="context.classes.input"
			class="form-select w-full !py-2 !px-4"
			:disabled="!!context.disabled"
			@change="onNativeChange"
		>
			<option v-if="context.placeholder" value="" disabled hidden>
				{{ context.placeholder }}
			</option>
			<option
				v-for="option in context.options"
				:key="option.value"
				:value="option.value"
			>
				{{ option.label }}
			</option>
		</select>
	</div>
	<div v-else ref="rootRef" class="relative" :class="context.classes.inner">
		<button
			:id="context.id"
			ref="buttonRef"
			type="button"
			class="flex items-center justify-between gap-2 w-full min-w-0 !py-2 !px-4"
			:class="[
				context.classes.input,
				{'border border-border-select !bg-background-primary': isOpen},
				{'text-body-primary': context._value},
			]"
			:disabled="!!context.disabled"
			:aria-expanded="isOpen"
			@click="toggleOpen()"
		>
			<span class="min-w-0 flex-1 text-left min-h-8 flex items-center">
				<ShoppingListCompactPreview
					v-if="selectedOption"
					:name="selectedOption.label"
					:items="selectedOption.items"
				/>
				<span v-else class="text-body-tertiary">
					{{ context.placeholder || 'Vybrat' }}
				</span>
			</span>
			<NuxtIcon
				:name="isOpen ? 'x' : 'chevron-down'"
				class="text-lg shrink-0 transition-transform"
				:class="isOpen ? 'text-icon-primary' : 'text-icon-tertiary'"
				filled
			/>
		</button>

		<ClientTeleport>
			<div
				v-show="isOpen"
				ref="dropdownRef"
				:style="{...floatingStyles, width: `${buttonWidth}px`}"
				class="absolute z-dropdown bg-background-primary rounded-lg shadow-sm border border-border-secondary typo-body-s"
			>
				<ul class="max-h-48 overflow-y-auto p-4 w-full" role="listbox">
					<li
						v-for="option in context.options"
						:key="option.value"
						class="cursor-pointer block py-2 rounded-lg focus-outline px-2.5 hover:bg-background-noticeable"
						:class="context._value === option.value ? 'text-body-important bg-background-noticeable' : 'text-body-secondary hover:text-body-important'"
						role="option"
						:aria-selected="context._value === option.value"
						tabindex="0"
						@click="selectOption(option.value)"
						@keydown.prevent.space.enter="selectOption(option.value)"
					>
						<ShoppingListCompactPreview
							:name="option.label"
							:items="option.items"
						/>
					</li>
				</ul>
			</div>
		</ClientTeleport>
	</div>
</template>

<script setup lang="ts">
	import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/vue';
	import {onClickOutside} from '@vueuse/core';
	import {storeToRefs} from 'pinia';
	import {useSelectboxStore} from '~/stores/selectbox';
	import type {FormkitShoppingListMergeSelect} from '~/types/formkitShoppingListMergeSelect';

	const props = defineProps<FormkitShoppingListMergeSelect>();

	const rootRef = ref<HTMLElement | null>(null);
	const buttonRef = ref<HTMLElement | null>(null);
	const dropdownRef = ref<HTMLElement | null>(null);
	const [isOpen, toggleOpen] = useToggle();
	const {isMobileOrTablet} = useDevice();
	const {width: buttonWidth} = useElementSize(buttonRef, undefined, {box: 'border-box'});
	const selectboxStore = useSelectboxStore();
	const {selectboxDropdown} = storeToRefs(selectboxStore);

	const {floatingStyles} = useFloating(buttonRef, dropdownRef, {
		placement: 'bottom-start',
		whileElementsMounted: autoUpdate,
		middleware: [offset(4), flip(), shift({padding: 20})],
	});

	const selectedOption = computed(() => (
		props.context.options?.find((option) => option.value === props.context._value)
	));

	onClickOutside(rootRef, () => {
		isOpen.value = false;
	}, {ignore: [dropdownRef, selectboxDropdown]});

	watch(isOpen, async (open) => {
		if (!open) {
			selectboxStore.setSelectboxDropdown(null);
			return;
		}
		await nextTick();
		selectboxStore.setSelectboxDropdown(dropdownRef.value);
	});

	onBeforeUnmount(() => {
		if (selectboxDropdown.value === dropdownRef.value) {
			selectboxStore.setSelectboxDropdown(null);
		}
	});

	function selectOption(value: string) {
		props.context.node.input(value);
		isOpen.value = false;
	}

	function onNativeChange(event: Event) {
		const value = (event.target as HTMLSelectElement).value;
		props.context.node.input(value || undefined);
	}
</script>
